import React from "react";
import styled from "styled-components";
import { get } from "lodash";
import { ArrowRight, SpeechBubbles } from "@sonder/ui/source/icons";
import { Activity } from "@sonder/ui/source/styled/components/Activity";
import { InputGroup } from "@sonder/ui/source/styled/fragments/InputGroup";
import {
  TaskActivityScheduleChangeMetadata,
  TaskActivity as TaskActivityTypes,
} from "core/queries/GetTaskComments";
import { ParsedText } from "tasks/components/ParsedText";
import { PRIORITY_ITEMS, PriorityBadge } from "tasks/components/PriorityBadge";

const parsePriority = (val: string | null) =>
  typeof val === "string" ? Number.parseInt(val, 10) : val;

const GuestNotificationActivityWrapper = styled.span`
  & span {
    display: flex;
    align-items: center;
  }

  & svg {
    margin-right: 0.5rem;
  }
`;

interface TaskActivityInterface {
  activity: TaskActivityTypes;
}
export const TaskActivity = ({ activity }: TaskActivityInterface) => {
  const name = get(activity, "author.name", null);

  switch (activity.activityType) {
    // prevent even the default status update from showing.
    case "permission_to_enter_change": {
      return null;
    }

    case "priority_change": {
      const nextPriority = PRIORITY_ITEMS.find(
        ({ value }) => value === parsePriority(activity.nextValue),
      );
      const nextPriorityString = (nextPriority && nextPriority.content) || null;
      const body = name
        ? `${name} changed priority to ${nextPriorityString}`
        : `Priority changed to ${nextPriorityString}`;

      return (
        <Activity body={body} createdAt={activity.activityTime}>
          <InputGroup>
            <PriorityBadge priority={parsePriority(activity.prevValue)} />
            <ArrowRight />
            <PriorityBadge priority={parsePriority(activity.nextValue)} />
          </InputGroup>
        </Activity>
      );
    }

    case "guest_notification": {
      return (
        <GuestNotificationActivityWrapper>
          <Activity
            body={
              <>
                <SpeechBubbles />
                <ParsedText>{activity.body}</ParsedText>
              </>
            }
            createdAt={activity.activityTime}
          />
        </GuestNotificationActivityWrapper>
      );
    }

    case "schedule_change": {
      const scheduleChangeMetadata =
        activity.metadata as TaskActivityScheduleChangeMetadata;
      const reason = scheduleChangeMetadata.reason;
      const previousAssignees = scheduleChangeMetadata.prevAssignees;
      const assigneeNames = previousAssignees?.map(assignee => assignee.name);

      let body = activity.body;

      if (assigneeNames && assigneeNames.length > 0) {
        const verb = assigneeNames.length > 1 ? "were" : "was";
        const userNames = assigneeNames.join(", ");

        body = `${name} ${reason} and ${userNames} ${verb} unassigned`;
      }

      return <Activity body={body} createdAt={activity.createdAt}></Activity>;
    }

    default: {
      if (activity.isSubtaskActivity) {
        return (
          <Activity body={activity.body} createdAt={activity.activityTime}>
            <InputGroup>
              <SpeechBubbles />
              <span>CGS Contacted Guest</span>
            </InputGroup>
          </Activity>
        );
      }
      return (
        <Activity
          body={<ParsedText>{activity.body}</ParsedText>}
          createdAt={activity.activityTime}
        />
      );
    }
  }
};
